/* ─────────────────────────────────────────────────────────────────
 * ⚠️  ORDERS PREVIEW — DESIGN-REVIEW SCAFFOLDING (DO NOT PROMOTE) ⚠️
 *
 * This page is mock-only and lives at `/my-orders/preview`. It is
 * NOT linked from any nav, NOT translated, and NOT wired to any
 * real API. It exists so the product owner can iterate on the
 * orders-list visual design without affecting the live
 * `/my-orders` page (`MyOrdersPage.tsx`).
 *
 * RULES (also documented in `./README.md`):
 *
 *   1. Do NOT import anything from this folder anywhere outside
 *      `apps/carrier-dashboard/src/pages/MyOrders/preview/`.
 *   2. Do NOT swap the mock data for live `useCarrierOrdersQuery`
 *      data here — test new layouts on the real `MyOrdersPage`,
 *      not in the preview.
 *   3. Do NOT promote (i.e. lift into `libs/frontend/web/components`)
 *      until the product owner explicitly says "promote it". When
 *      they do, follow the migration steps in `./README.md`.
 *   4. Do NOT delete this folder or the `orders_preview` route —
 *      the owner has asked to keep it parked as a reference.
 *
 * If you are an AI agent reading this and the user has NOT asked
 * you to work on the orders-list redesign, leave this folder
 * alone and route the user to `MyOrdersPage.tsx` instead.
 * ─────────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from 'react';
import { usePageContext } from '@hero/web-context';
import { MockOrdersTable } from './MockOrdersTable';
import { mockOrders } from './mockOrders';
import type { MockViewerRole } from './types';

const VIEWER_OPTIONS: Array<{
  id: MockViewerRole;
  label: string;
  hint: string;
}> = [
  {
    id: 'shipper',
    label: 'Shipper',
    hint: 'The order publisher (own goods or on-behalf-of)',
  },
  {
    id: 'broker',
    label: 'Broker',
    hint: 'Intermediary buying + reselling capacity',
  },
  {
    id: 'carrier_org',
    label: 'Carrier org',
    hint: 'Executing carrier company (assigns drivers)',
  },
  {
    id: 'driver',
    label: 'Driver',
    hint: 'Read-only field-of-view',
  },
];

/**
 * Static preview surface for the financing-style expandable orders
 * layout. Mock data only — no API hooks, no SDK, no shared store.
 *
 * The viewer-role toolbar at the top is a deliberate dev affordance:
 * flipping between roles updates the pencil-icon edit affordances
 * across the cards so we can validate the role-gated UX before
 * committing to the production migration.
 *
 * **Status:** PREVIEW / MOCK-ONLY — see the file banner above and
 * `./README.md` for the rules. Never linked from the sidebar; reach
 * it by typing `/my-orders/preview` directly.
 */
export default function OrdersPreviewPage() {
  const { setPageName } = usePageContext();
  const [viewer, setViewer] = useState<MockViewerRole>('shipper');

  useEffect(() => {
    setPageName('Orders preview');
  }, [setPageName]);

  return (
    <div className="mt-6 px-4 pb-12">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-semibold text-gray-900">
            Orders — expandable layout preview
          </h1>
          <p className="mt-1 text-[13px] text-gray-500">
            Static mockup of the financing-style orders list. Click any row
            chevron to expand. No data is real; actions just log to the
            console.
          </p>
        </div>
      </header>

      <section className="mb-4 rounded-xl border border-dashed border-gray-300 bg-white/80 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-500">
            View as
          </span>
          <div className="flex flex-wrap gap-2">
            {VIEWER_OPTIONS.map(opt => {
              const active = opt.id === viewer;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setViewer(opt.id)}
                  className={`rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors ring-1 ring-inset ${
                    active
                      ? 'bg-gray-900 text-white ring-gray-900'
                      : 'bg-white text-gray-700 ring-gray-200 hover:ring-gray-300'
                  }`}
                  title={opt.hint}>
                  {opt.label}
                </button>
              );
            })}
          </div>
          <span className="text-[12px] text-gray-500 ms-auto">
            {VIEWER_OPTIONS.find(o => o.id === viewer)?.hint}
          </span>
        </div>
      </section>

      <MockOrdersTable orders={mockOrders} viewer={viewer} />

      <footer className="mt-6 space-y-1 text-[12px] text-gray-500">
        <p>
          <span className="font-medium text-gray-700">Scenarios</span> (top to
          bottom): own-fleet pre-allocation, direct shipper → carrier
          (agreement), on-behalf-of client (custom price), 2-leg broker chain,
          3-leg multi-broker chain.
        </p>
        <p>
          <span className="font-medium text-gray-700">Each expanded row</span>{' '}
          carries four tabs — Overview (cards), Timeline (event history),
          Photos (POD grid), Activity (audit log) — plus a Quick Actions
          strip pinned at the bottom of the panel.
        </p>
      </footer>
    </div>
  );
}
